import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Monitor, Tablet, Smartphone, RotateCcw, Save, History } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  grid_mobile: ["Product columns on phones", { 1: "1 column", 2: "2 columns" }],
  grid_tablet: ["Product columns on tablets", { 2: "2 columns", 3: "3 columns" }],
  grid_desktop: ["Product columns on desktop", { 4: "4 columns", 5: "5 columns" }],
  hero_mode: ["Homepage slide height", { fixed: "Fixed height (best for text slides)", banner: "Exact 1600 × 460 banner proportions" }],
  spacing: ["Spacing between products", { compact: "Compact", normal: "Normal", relaxed: "Relaxed" }],
  font_scale: ["Storefront text size", { 90: "Smaller (90%)", 100: "Standard (100%)", 110: "Larger (110%)", 120: "Largest (120%)" }],
  header_mobile_logo: ["Logo on phones", { compact: "Compact", full: "Larger" }],
};
const DEVICES = [["mobile", 390, Smartphone], ["tablet", 768, Tablet], ["desktop", 1280, Monitor]];
const PAGES = [["/", "Homepage"], ["/search/printer", "Search results"], ["/category/eftpos", "Category"]];

export default function ResponsiveTab() {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dev, setDev] = useState("mobile");
  const [page, setPage] = useState("/");
  const [frameKey, setFrameKey] = useState(0);
  const load = () => api.get("/admin/site/responsive").then((r) => { setDoc(r.data); setDraft(r.data.current); });
  useEffect(() => { load(); }, []);
  const src = useMemo(() => draft && `${page}?rd_preview=${btoa(JSON.stringify(draft))}`, [draft, page]);
  if (!doc || !draft) return <p className="text-muted-foreground">Loading…</p>;
  const changed = JSON.stringify(draft) !== JSON.stringify(doc.current);
  const run = async (p, msg) => { try { const r = await p; setDoc({ ...doc, ...r.data }); setDraft(r.data.current); toast.success(msg); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const [, width] = DEVICES.find((d) => d[0] === dev);
  const scale = Math.min(1, 560 / width);
  return (
    <div className="space-y-5" data-testid="responsive-tab">
      <div><h1 className="text-2xl sm:text-3xl font-extrabold mb-1">Responsive Design</h1>
        <p className="text-muted-foreground text-sm">Safe layout controls for phones, tablets and desktop. Preview changes, then Publish. The last 10 published versions can be restored.</p></div>
      <div className="grid xl:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border bg-white p-4 space-y-3">
            {Object.entries(LABELS).map(([k, [label, opts]]) => (
              <div key={k}><Label className="text-xs">{label}</Label>
                <select value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: isNaN(e.target.value) ? e.target.value : Number(e.target.value) })}
                  className="h-10 w-full rounded-md border bg-white px-2 text-sm" data-testid={`rd-${k}`}>
                  {Object.entries(opts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run(api.put("/admin/site/responsive", draft), "Published")} disabled={!changed} className="bg-accent text-steel font-semibold" data-testid="rd-publish"><Save size={15} className="mr-1.5" /> Publish</Button>
            <Button variant="outline" onClick={() => setDraft(doc.current)} disabled={!changed} data-testid="rd-discard">Discard changes</Button>
            <Button variant="outline" onClick={() => window.confirm("Restore all responsive settings to defaults?") && run(api.post("/admin/site/responsive/restore-defaults"), "Defaults restored")} data-testid="rd-restore"><RotateCcw size={15} className="mr-1.5" /> Restore defaults</Button>
          </div>
          {changed && <p className="text-xs text-amber-700" data-testid="rd-unpublished">Unpublished changes — visible only in the preview.</p>}
          <div className="rounded-xl border bg-white p-4" data-testid="rd-versions">
            <div className="flex items-center gap-2 font-semibold text-sm text-steel mb-2"><History size={15} /> Version history</div>
            {doc.versions.length === 0 ? <p className="text-xs text-muted-foreground">No earlier versions yet.</p> : doc.versions.map((v) => (
              <div key={v.id} className="flex items-center gap-2 py-1.5 border-t text-xs">
                <span className="flex-1">{new Date(v.at).toLocaleString()} · {v.note} · {v.by}</span>
                <button className="text-accent font-semibold hover:underline" onClick={() => window.confirm("Restore this version?") && run(api.post(`/admin/site/responsive/rollback/${v.id}`), "Version restored")} data-testid={`rd-rollback-${v.id}`}>Restore</button>
              </div>))}
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="inline-flex rounded-lg border p-1">{DEVICES.map(([k, w, Icon]) => (
              <button key={k} onClick={() => setDev(k)} aria-label={`${k} preview`} data-testid={`rd-preview-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm capitalize ${dev === k ? "bg-steel text-white" : ""}`}><Icon size={15} />{k} <span className="text-xs opacity-70">{w}px</span></button>))}</div>
            <select value={page} onChange={(e) => setPage(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="rd-preview-page">
              {PAGES.map(([p, l]) => <option key={p} value={p}>{l}</option>)}</select>
            <Button size="sm" variant="outline" onClick={() => setFrameKey(frameKey + 1)}>Reload</Button>
          </div>
          <div className="overflow-hidden rounded-lg border bg-slate-100 mx-auto" style={{ width: width * scale, height: 720 * scale }}>
            <iframe key={`${frameKey}-${src}-${dev}`} title={`${dev} preview`} src={src} data-testid="rd-preview-frame"
              style={{ width, height: 720, transform: `scale(${scale})`, transformOrigin: "0 0", border: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
